import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import { Trash2, UserPlus } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useParterManagedPayrollError } from 'components/hooks/payroll/usePartnerManagedPayrollError'
import { useGetPayScheduleEmployees } from 'components/hooks/useGetPayScheduleEmployees'
import { useRemoveEmployeePaySchedule } from 'components/hooks/useRemoveEmployeePaySchedule'
import { TableSearchSingleColumn } from 'components/reusable/table/TableSearchSingleColumn'
import TanstackTable, { type PaginationMetadata } from 'components/reusable/table/TanstackTable'
import { Button, IconButton } from 'slab'
import { type Employee } from 'types'
import { PayrollPayScheduleSource } from 'utils/web_api.schemas'

import { AddEmployeesToScheduleSheet } from './AddEmployeesToScheduleSheet'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip'
import { type PaySchedule } from '../../types/payroll'

type EmployeePayScheduleFormProps = {
  paySchedule: PaySchedule
}

const columnHelper = createColumnHelper<Employee>()

export const EmployeePayScheduleForm = ({ paySchedule }: EmployeePayScheduleFormProps) => {
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: 10,
  })
  const { data: employees, isLoading: loadingEmployees } = useGetPayScheduleEmployees({ payScheduleId: paySchedule.id, pagination, search: searchQuery })
  const [open, setOpen] = useState(false)
  const [paginationMetadata, setPaginationMetadata] = useState<PaginationMetadata | undefined>()
  const { mutate: removeEmployeePaySchedule } = useRemoveEmployeePaySchedule()
  const { showPartnerManagedError } = useParterManagedPayrollError()

  const columns = [
    columnHelper.accessor((row) => `${row.first_name} ${row.last_name}`, {
      id: 'name',
      header: 'Name',
      cell: (info) => info.getValue(),
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: (info) => (
        <div className='flex justify-end'>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton
                  icon={Trash2}
                  label='Remove employee from schedule'
                  onClick={() =>
                    removeEmployeePaySchedule({
                      payScheduleId: paySchedule.id,
                      employeeId: info.row.original.id,
                    })}
                  variant='destructive'
                />
              </TooltipTrigger>
              <TooltipContent>
                <p>Remove employee from schedule</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    }),
  ]

  useEffect(() => {
    if (employees) {
      setPaginationMetadata(employees.pagination)
    }
  }, [employees?.pagination])

  const table = useReactTable({
    columns,
    data: employees?.data || [],
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: true,
    enableMultiRowSelection: true,
    enableColumnResizing: false,
    pageCount: employees?.pagination.total_pages,
    onPaginationChange: setPagination,
    manualPagination: true,
    rowCount: employees?.pagination.total_records,
    state: {
      pagination,
    },
  })

  const handleAddEmployeesButton = () => {
    if (paySchedule.source === PayrollPayScheduleSource.partner) {
      showPartnerManagedError('Cannot add employees')
    } else {
      setOpen(true)
    }
  }

  return (
    <div className='p-6'>
      <div className='space-y-4'>
        <TableSearchSingleColumn
          table={table}
          columnId='name'
          placeholder='Find an employee'
          inputClassName='border-none w-full'
          containerClassName='shadow-none'
          onChange={setSearchQuery}
        />

        <div className='flex justify-end'>
          <Button
            leftIcon={UserPlus}
            variant='tertiary'
            onClick={handleAddEmployeesButton}
          >
            Add employees
          </Button>
        </div>
      </div>

      <TanstackTable
        table={table}
        hideTableOutlines
        isLoading={loadingEmployees}
        isError={false}
        headerRowClasses='border-none bg-white text-[#737373] text-xs'
        noDataMessage='No employees assigned to this pay schedule'
        paginationLabelSingular='employee'
        paginationLabelPlural='employees'
        tableClasses='table-fixed'
        rowClasses='break-words overflow-wrap-anywhere !whitespace-normal'
        paginationMetadata={paginationMetadata}
        onNextPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.next_page || 0,
          })}
        onPreviousPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.prev_page || 0,
          })}
      />
      <AddEmployeesToScheduleSheet
        paySchedule={paySchedule}
        open={open}
        onOpenChange={setOpen}
      />
    </div>
  )
}
